<template>
  <SignInSignUpLayout>
    <b-form
      @submit="setUpAgency"
      class="sing-in-form position-relative"
    >
      <Preloader v-if="isLoading" />

      <h1 class="sign-in-title">{{ title }}</h1>

      <Description>
        To help us tailor our services to better meet your needs, we'd like
        to gather some additional information about your company.
      </Description>

      <SignUpMultiselect
        v-if="shouldShowJiraSiteSelect"
        v-model="selectedJiraSite"
        id="jira-site-select"
        label="Select your Jira site"
        :options="availableJiraSites"
        track-by="id"
        optionLabel="name"
        placeholder="Select Jira site"
        :state="jiraSiteState"
        groupClass="jira-site-select-group"
      >
        <template #invalid-feedback>
          <div v-if="!$v.selectedJiraSite.requiredWhenMultipleSites">{{ VALIDATION_MESSAGES.common.required }}</div>
          <div v-else v-for="(error, index) in jiraSiteServerErrors" :key="index">
            {{ error }}
          </div>
        </template>
      </SignUpMultiselect>

      <CompanyUrlPrefixField
        v-if="showPrefixField"
        ref="prefixComponent"
        :companyPrefixGroupId="companyPrefixGroupId"
        :hideValidationErrors="!submitButtonPressed"
        :serverErrors="prefixServerErrors"
      />

      <b-form-group :state="agencyNameState">
        <label class="form__label input-label" for="agency-name">Agency Name</label>
        <b-form-input
          v-model="agencyName"
          id="agency-name"
          class="sing-up-input"
          placeholder="Enter agency name"
          autocomplete="off"
          :state="agencyNameState"
        />
        <template #invalid-feedback>
          <div v-if="!$v.agencyName.required">{{ VALIDATION_MESSAGES.common.required }}</div>
          <div v-else-if="!$v.agencyName.minLength">{{ VALIDATION_MESSAGES.common.minLength($v.agencyName.$params.minLength.min) }}</div>
          <div v-else-if="!$v.agencyName.maxLength">{{ VALIDATION_MESSAGES.common.maxLength($v.agencyName.$params.maxLength.max) }}</div>
          <div v-else-if="!$v.agencyName.atLeastOneLetter">{{ VALIDATION_MESSAGES.common.atLeastOneLetter }}</div>
          <div v-else v-for="(error, index) in agencyNameServerErrors" :key="index">
            {{ error }}
          </div>
        </template>
      </b-form-group>

      <SignUpMultiselect
        groupClass="country-select"
        v-model="country"
        id="country-select"
        label="Country"
        :options="getCountries"
        track-by="code"
        optionLabel="name"
        placeholder="Select country"
        :state="countryState"
      >
        <template #invalid-feedback>
          <div v-if="!$v.country.required">{{ VALIDATION_MESSAGES.common.required }}</div>
          <div v-else v-for="(error, index) in countryServerErrors" :key="index">
            {{ error }}
          </div>
        </template>
      </SignUpMultiselect>

      <SignUpMultiselectLight
        v-model="timezone"
        :options="getTimeZones"
        id="time-zone-select"
        label="Time Zone"
        track-by="timezone"
        optionLabel="label"
        placeholder="Select timezone"
        :state="timezoneState"
      >
        <template #invalid-feedback>
          <div v-if="!$v.timezone.required">{{ VALIDATION_MESSAGES.common.required }}</div>
          <div v-else v-for="(error, index) in timezoneServerErrors" :key="index">
            {{ error }}
          </div>
        </template>
      </SignUpMultiselectLight>

      <SignUpMultiselectLight
        groupClass="currency-select"
        v-model="currency"
        :options="currencies"
        id="currency-select"
        label="Currency"
        track-by="code"
        optionLabel="name"
        placeholder="Select currency"
        :state="currencyState"
      >
        <template #invalid-feedback>
          <div v-if="!$v.currency.required">{{ VALIDATION_MESSAGES.common.required }}</div>
          <div v-else v-for="(error, index) in currencyServerErrors" :key="index">
            {{ error }}
          </div>
        </template>
      </SignUpMultiselectLight>

      <SignUpMultiselect
        v-model="companySize"
        id="company-size-select"
        label="Company size"
        :options="companySizesList"
        track-by="value"
        optionLabel="label"
        placeholder="Select company size"
        :state="companySizeState"
      >
        <template #invalid-feedback>
          <div v-if="!$v.companySize.required">{{ VALIDATION_MESSAGES.common.required }}</div>
          <div v-else v-for="(error, index) in companySizeServerErrors" :key="index">
            {{ error }}
          </div>
        </template>
      </SignUpMultiselect>

      <b-button class="button confirm-button" type="submit" variant="primary" :disabled="isSubmitButtonDisabled">
        Confirm
      </b-button>

      <slot name="progress-bar"></slot>
    </b-form>
  </SignInSignUpLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { COMPLETE_SIGN_UP_STEP, COMPLETE_SIGN_UP_USER_SPECIFIED_COMPANY_SIZE } from '@/constants/localStorageKeys'
import { atLeastOneUnicodeLetter } from '@utils/validators'
import { COUNTRY_CODE_TO_CURRENCY } from '@/constants/countryAndCurrency'
import Preloader from '@components/Preloader.vue'
import SignInSignUpLayout from '@components/sign-up/SignInSignUpLayout.vue'
import Description from '@components/sign-up/Description.vue'
import SignUpMultiselect from '@components/sign-up/SignUpMultiselect.vue'
import SignUpMultiselectLight from '@components/sign-up/SignUpMultiselectLight.vue'
import CompanyUrlPrefixField from '@components/sign-up/CompanyUrlPrefixField.vue'

export default {
  name: 'AddAgencyForm',
  mixins: [ validationMixin ],
  components: {
    Preloader,
    SignInSignUpLayout,
    Description,
    SignUpMultiselect,
    SignUpMultiselectLight,
    CompanyUrlPrefixField
  },
  props: {
    submitUrl: {
      type: String,
      required: true
    },
    onSuccessfulSubmit: {
      type: Function,
      required: true
    },
    showPrefixField: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Add your agency'
    }
  },
  data () {
    /**
     * If you ever want to change the format in which the values for company size are store, be aware that "Invite Teammates"
     * step relies on this format. "Add Agency" step saves company size value to the localStorage and "Invite Teammates"
     * step reads that value.
     */
    const companySizesList = [
      { value: '10', label: '1-10' },
      { value: '50', label: '11-50' },
      { value: '100', label: '51-100' },
      { value: '200', label: '101-200' },
      { value: '201', label: '201+' }
    ]

    return {
      companyPrefixGroupId: 'company-prefix-group',
      agencyName: '',
      country: null,
      timezone: null,
      currency: null,
      companySizesList,
      companySize: companySizesList[0],
      availableJiraSites: [],
      selectedJiraSite: null,

      prefixServerErrors: null,
      agencyNameServerErrors: null,
      countryServerErrors: null,
      timezoneServerErrors: null,
      currencyServerErrors: null,
      companySizeServerErrors: null,
      jiraSiteServerErrors: null,

      isLoading: false,
      submitButtonPressed: false,
      submitRequestPending: false
    }
  },
  validations: {
    agencyName: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(100),
      atLeastOneLetter: atLeastOneUnicodeLetter
    },
    country: { required },
    timezone: { required },
    currency: { required },
    companySize: { required },
    selectedJiraSite: {
      requiredWhenMultipleSites: function (value) {
        if (!this.shouldShowJiraSiteSelect) {
          return true // Not required when only one site
        }
        return value != null && value !== '' // Required when multiple sites
      }
    }
  },
  async created () {
    const agencyName = this.$cookies.get('tenant_agency_name')

    if (agencyName) {
      this.agencyName = agencyName
    }

    this.isLoading = true
    await Promise.all([
      this.fetchLists(),
      this.fetchAtlassianResources()
    ]).finally(() => {
      this.isLoading = false
    })

    // After fetchAtlassianResources, if a Jira site is auto-selected, pre-fill fields
    // This part is for initial load. The watch property handles subsequent user selections.
    if (this.selectedJiraSite) {
      this.agencyName = this.selectedJiraSite.name
      this.$nextTick(() => {
        if (this.$refs.prefixComponent && this.selectedJiraSite.url) {
          try {
            const url = new URL(this.selectedJiraSite.url)
            const parts = url.hostname.split('.')
            if (parts.length > 0) {
              this.$refs.prefixComponent.prefix = parts[0]
            }
          } catch (e) {
            console.error('Invalid URL for Jira site:', this.selectedJiraSite.url, e)
          }
        }
      })
    }

    let userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    if (userTimeZone === 'Europe/Kiev') {
      userTimeZone = 'Europe/Kyiv'
    }

    this.timezone = this.getTimeZones.find(timezone => timezone.timezone === userTimeZone)

    this.country = this.getCountries.find(country => {
      return country.code === this.timezone?.country_code
    })

    if (this.country) {
      const currencyCode = COUNTRY_CODE_TO_CURRENCY[this.country.code]
      if (currencyCode) {
        this.currency = this.currencies.find(currency => currency.code === currencyCode)
      } else {
        this.currency = this.currencies.find(currency => currency.code === 'USD')
      }
    }
  },
  computed: {
    ...mapGetters({
      getCountries: 'countries/getCountries',
      getTimeZones: 'countries/getTimeZones',
      getAllCurrencies: 'getCurrencies'
    }),
    currencies () {
      return this.getAllCurrencies.map(({ code, name }) => {
        return {
          code,
          name: `${name} (${code})`
        }
      })
    },
    agencyNameState () {
      return this.submitButtonPressed && this.$v.agencyName.$invalid ? false : null
    },
    countryState () {
      return this.submitButtonPressed && this.$v.country.$invalid ? false : null
    },
    timezoneState () {
      return this.submitButtonPressed && this.$v.timezone.$invalid ? false : null
    },
    currencyState () {
      return this.submitButtonPressed && this.$v.currency.$invalid ? false : null
    },
    companySizeState () {
      return this.submitButtonPressed && this.$v.companySize.$invalid ? false : null
    },
    jiraSiteState () {
      return this.submitButtonPressed && this.$v.selectedJiraSite.$invalid ? false : null
    },
    shouldShowJiraSiteSelect () {
      return this.availableJiraSites.length > 1
    },
    isFormValid () {
      // Check individual field validations, skipping selectedJiraSite if not required
      const fieldsValid = this.$v.agencyName.$invalid === false &&
                         this.$v.country.$invalid === false &&
                         this.$v.timezone.$invalid === false &&
                         this.$v.currency.$invalid === false &&
                         this.$v.companySize.$invalid === false &&
                         (!this.shouldShowJiraSiteSelect || this.$v.selectedJiraSite.$invalid === false)

      const prefixValid = !this.showPrefixField || !this.$refs.prefixComponent?.$v.$invalid
      return fieldsValid && prefixValid
    },
    isSubmitButtonDisabled () {
      return this.submitRequestPending || (this.submitButtonPressed && !this.isFormValid)
    }
  },
  watch: {
    selectedJiraSite (newVal) {
      if (newVal) {
        // Update agency name
        this.agencyName = newVal.name

        // Update company URL prefix if showPrefixField is true
        this.$nextTick(() => {
          if (this.$refs.prefixComponent && newVal.url) {
            try {
              const url = new URL(newVal.url)
              const parts = url.hostname.split('.')
              if (parts.length > 0) {
                this.$refs.prefixComponent.prefix = parts[0]
              }
            } catch (e) {
              console.error('Invalid URL for Jira site:', newVal.url, e)
            }
          }
        })
      } else {
        // Optionally clear fields if nothing is selected (e.g., if multiselect allows clearing)
        this.agencyName = ''
        if (this.$refs.prefixComponent) {
          this.$refs.prefixComponent.prefix = ''
        }
      }
    }
  },
  methods: {
    async fetchLists () {
      return Promise.all([
        this.$api.country.getCountries(),
        this.$api.country.getTimeZones(),
        this.$api.finances.getCurrencies()
      ]).catch(error => {
        this.errorNotifications(error)
      })
    },
    async fetchAtlassianResources () {
      try {
        const response = await this.axios.get('/api/register/get-atlassian-available-resources')
        this.availableJiraSites = response.data || []

        // Auto-select if only one site available
        if (this.availableJiraSites.length === 1) {
          this.selectedJiraSite = this.availableJiraSites[0]
        }
      } catch (error) {
        // Silently fail if no resources available
        this.availableJiraSites = []
      }
    },
    formTouch () {
      this.$v.$touch()
      if (this.$refs.prefixComponent) {
        this.$refs.prefixComponent.$v.$touch()
      }
    },
    setUpAgency (e) {
      e.preventDefault()

      this.submitButtonPressed = true

      // clear server errors
      this.prefixServerErrors = null
      this.agencyNameServerErrors = null
      this.countryServerErrors = null
      this.timezoneServerErrors = null
      this.currencyServerErrors = null
      this.companySizeServerErrors = null
      this.jiraSiteServerErrors = null

      this.formTouch()
      if (!this.isFormValid) {
        this.submitRequestPending = false
        return
      }

      this.submitRequestPending = true

      this.axios
        .post(this.submitUrl, {
          ...(this.showPrefixField ? { prefix: this.$refs.prefixComponent.prefix } : {}),
          agency_name: this.agencyName.trim(),
          country_code: this.country.code,
          time_zone: this.timezone ? this.timezone.timezone : null,
          currency_code: this.currency.code,
          company_size: this.companySize.value,
          ...(this.selectedJiraSite ? { cloud_id: this.selectedJiraSite.id } : {})
        })
        .then(() => {
          localStorage.removeItem(COMPLETE_SIGN_UP_STEP)
          localStorage.setItem(COMPLETE_SIGN_UP_USER_SPECIFIED_COMPANY_SIZE, this.companySize.value)
          this.onSuccessfulSubmit()
        })
        .catch((error) => {
          if (error?.response?.data?.errors) {
            /* eslint-disable camelcase */
            const { prefix, agency_name, country_code, time_zone, currency_code, company_size, cloud_id, ...restErrors } = error.response.data.errors

            if (prefix) this.prefixServerErrors = prefix
            if (agency_name) this.agencyNameServerErrors = agency_name
            if (country_code) this.countryServerErrors = country_code
            if (time_zone) this.timezoneServerErrors = time_zone
            if (currency_code) this.currencyServerErrors = currency_code
            if (company_size) this.companySizeServerErrors = company_size
            if (cloud_id) this.jiraSiteServerErrors = cloud_id
            /* eslint-enable camelcase */

            const errors = Object.values(restErrors)
            for (let i = 0; i < errors.length; i++) {
              this.$snotify.error(errors[i][0], 'Error', this.$store.state.snotify)
            }
          } else {
            console.error(error)
            this.$snotify.error(error, 'Error', this.$store.state.snotify)
          }
        })
        .finally(() => {
          this.submitRequestPending = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.sign-in-title {
  line-height: 48px !important;
  margin-bottom: 10px !important;
}

.country-select {
  margin-bottom: 7px !important;
}

.currency-select {
  margin-bottom: 30px !important;
}

// Override capitalize for Jira site select to preserve original casing
::v-deep #jira-site-select .multiselect__option,
::v-deep #jira-site-select .multiselect__single {
  text-transform: none !important;
}

// Alternative approach with more specific selectors
::v-deep .multiselect-form-group #jira-site-select .multiselect__option {
  text-transform: none !important;
}

::v-deep .multiselect-form-group #jira-site-select .multiselect__single {
  text-transform: none !important;
}
</style>

<style lang="scss">
// Global CSS to override capitalize for Jira site select
#jira-site-select .multiselect__option,
#jira-site-select .multiselect__single,
.jira-site-select-group .multiselect__option,
.jira-site-select-group .multiselect__single {
  text-transform: none !important;
}

</style>
